import React from 'react';

import { Layout } from 'components/Layout';
import { Hero } from 'components/sections/Hero';
import { companyHero } from 'content/hero';
import { Colors } from 'enums/Colors';
import { Card } from 'components/Card';
import { ImagePosition } from 'components/Card/Card';
import { MarketplaceAdsResponse } from 'types/MarketplaceAdsResponse';
import { reviews } from 'content/reviews';
import { Reviews } from 'components/sections/Reviews';
import { Proof } from 'components/sections/Proof';
import { proofCompany } from 'content/mocked/proof';
import { Magazine, MagazineArticleCard } from 'components/sections/Magazine';
import { Faq, Topic } from 'components/sections/Faq';
import { Gutter } from 'components/Gutter';
import {
  companyReleaseCardsList,
  companyLeasingCardsList,
  companySellCardsList,
} from 'content/cardsList';

import styles from './Company.module.scss';

interface Props {
  articles: MagazineArticleCard[];
  faqCompanyLeasing: {
    title: string;
    questions: Topic[];
  };
  marketplaceAds: MarketplaceAdsResponse | undefined;
  isLoading: boolean;
  error: any;
}

export const Company: React.FC<Props> = ({
  articles,
  faqCompanyLeasing,
  marketplaceAds,
  error,
  isLoading,
}) => {
  const hasDataResult = !error && !isLoading && marketplaceAds?.payload?.items.length;

  return (
    <Layout>
      <Hero
        title={companyHero.title}
        text={companyHero.text}
        mobileSrc={companyHero.mobileSrc}
        desktopSrc={companyHero.desktopSrc}
        alt={companyHero.alt}
        variant="full-length"
      />
      <Gutter size="negative">
        <div className={styles.carouselWrapper}>
          <div className={styles.carousel}>
            <Card
              title="Nyhet: Release"
              description="Med Blipp kan du lägga om ditt nuvarande leasingavtal och se över dina villkor med hjälp av professionella rådgivare."
              imagePosition={ImagePosition.None}
              color={Colors.Yellowish}
              type="fat"
              bulletList={companyReleaseCardsList}
              action="Läs mer om Release"
              href="/foretag/release"
              buttonColor={Colors.DarkBlue}
            />

            <Card
              title="Företagsleasing"
              description="Med finansiering via Blipp kan du enkelt skaffa en bil som säljs av ett annat företag med en ny företagsleasing eller avbetalning som passar dig."
              imagePosition={ImagePosition.None}
              color={Colors.MintSweet}
              type="fat"
              bulletList={companyLeasingCardsList}
              action="Läs mer om företagsleasing"
              href="/foretag/foretagsleasing"
              buttonColor={Colors.DarkBlue}
            />

            <Card
              title="Sälja företagsbil"
              description="Möjlighet att spara pengar genom att sälja era företagsbilar via Blipp istället för genom bilhandlaren."
              imagePosition={ImagePosition.None}
              color={Colors.SkyBlue}
              type="fat"
              bulletList={companySellCardsList}
              action="Läs mer om att sälja"
              href="/salja-begagnad-bil"
              buttonColor={Colors.DarkBlue}
            />
          </div>
        </div>
      </Gutter>
      <Gutter size="medium">
        {hasDataResult ? (
          marketplaceAds.payload &&
          marketplaceAds.payload.items && (
            <Proof
              subTitle="Bilar som kan företagsleasas"
              cars={marketplaceAds.payload.items}
              buttons={false}
              blurbs={proofCompany.blurbs}
              urls={proofCompany.urls}
            />
          )
        ) : (
          <div className={`${styles['loading-wrapper']} ${styles.box}`}>
            <div className={styles.box}>
              <video autoPlay loop muted playsInline>
                <source src="/assets/videos/blipp-logotyp-azur-animation.mp4" type="video/mp4" />
              </video>

              <p>Laddar...</p>
            </div>
          </div>
        )}
      </Gutter>
      <Gutter size="medium">
        <Faq title={faqCompanyLeasing.title} questions={faqCompanyLeasing.questions} />
      </Gutter>
      <Gutter size="large">
        <Reviews title={reviews.title} reviews={reviews.reviews} />
      </Gutter>
      <Gutter size="large">
        <Magazine articles={articles} />
      </Gutter>
    </Layout>
  );
};
